import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import type { ApiResponse } from '../types/api';
import httpClient from './http/httpClient';

export const generateAdHocDocument = async (params: {
  userId: string;
  tenderId: string;
  docFileName: string; // The originalFilename is often used as ID
  clientId: string;
  isStrictForm?: boolean | undefined;
  jobId?: string;
  confidenceThreshold?: number;
  flagUncertainAnswers?: boolean;
}) => {
  const response = await httpClient.post<ApiResponse<{ message: string }>>(
    '/generateAdHocDocument',
    params,
    {
      headers: {},
      timeout: 0,
    }
  );
  return response;
};
export const regenerateDocument = async (params: {
  clientId: string;
  userId: string;
  tenderId: string;
  userSpecificBucket: string;
  docType: string;
  sourceDraftStorageUri?: string;
  sourceDraftGcsPath?: string; // Deprecated
  sourceDraftFileName?: string;
  reviewSource?: 'upload' | 'sections' | undefined;
}) => {
  const response = await httpClient.post<ApiResponse<unknown>>(
    '/regenerateDocumentHandler',
    params,
    {
      headers: {},
      timeout: 0,
    }
  );
  return response;
};

export const generateCVs = async (params: {
  userId: string;
  tenderId: string;
  clientId: string;
  cv_files?: string[];
  jobId?: string;
}) => {
  const response = await httpClient.post<
    ApiResponse<{ success: boolean; mode: string; uploaded_uris?: string[] }>
  >('/cvProcessorService', params, {
    headers: {},
    timeout: 0,
  });
  return response;
};

// New function to report system errors to admin

export interface CompileDraftResponse {
  success: boolean;
  path?: string;
  downloadUrl?: string;
  message?: string;
}

export const compileDraft = async (tenderId: string, clientId: string, formFilename?: string) => {
  const response = await httpClient.post<CompileDraftResponse>(
    '/compileDraft',
    {
      tenderId,
      clientId,
      formFilename,
    },
    {
      timeout: 0, // No frontend timeout
    }
  );
  return response;
};

export interface CompileSectionResponse {
  success: boolean;
  path?: string;
  message?: string;
}

export interface GenerateVisualExportResponse {
  success: boolean;
  link?: string;
  error?: string;
}

export const generateVisualExport = async (
  tenderId: string,
  clientId: string,
  exportType: 'presentation' | 'pdf',
  exportFormat?: 'pptx' | 'pdf' | 'html',
  numSlides?: number,
  engine?: string,
  jobId?: string
) => {
  const response = await httpClient.post<GenerateVisualExportResponse>(
    '/generateVisualExport',
    {
      tenderId,
      clientId,
      exportType,
      exportFormat,
      numSlides,
      engine,
      jobId,
    },
    {
      timeout: 0, // No frontend timeout, let the backend dictate completion
    }
  );
  return response;
};

export const compileSectionDocx = async (tenderId: string, sectionId: string, clientId: string) => {
  const response = await httpClient.post<CompileSectionResponse>(
    '/compileSectionDocx',
    {
      tenderId,
      sectionId,
      clientId,
    },
    {
      timeout: 0,
    }
  );
  return response;
};

export const tenderInquiry = async (params: {
  tenderId: string;
  message: string;
  history: unknown[];
  clientId: string;
  teamId?: string;
}) => {
  const response = await httpClient.post<{
    success: boolean;
    response: string;
  }>(
    '/tenderInquiry',
    {
      ...params,
    },
    {
      headers: {},
      timeout: 0,
    }
  );
  return response;
};

export interface ImpactItem {
  section_id: string;
  section_title?: string;
  urgency: 'high' | 'medium' | 'low';
  instruction: string | Record<string, unknown>;
}

export const sectionChat = async (params: {
  tenderId: string;
  sectionId: string;
  message: string;
  currentContent: string;
  history: unknown[];
  clientId: string;
  teamId?: string;
}) => {
  const response = await httpClient.post<{
    success: boolean;
    response: string;
    updatedContent?: string;
    impact_report?: ImpactItem[];
  }>(
    '/sectionChat',
    {
      ...params,
    },
    { timeout: 0 } // No frontend timeout
  );
  return response;
};

export const applyImpactFix = async (params: {
  tenderId: string;
  sectionId: string; // The ID of the section to fix
  instruction: string;
  clientId: string;
  userId: string;
  teamId?: string;
}) => {
  const response = await httpClient.post<{ success: boolean; message: string; error?: string }>(
    '/sectionChat',
    {
      action: 'apply_autofix',
      tenderId: params.tenderId,
      sectionId: params.sectionId,
      instruction: params.instruction,
      clientId: params.clientId,
      userId: params.userId,
      teamId: params.teamId,
    },
    { timeout: 0 } // No frontend timeout
  );
  return response;
};

export const applyBatchFixes = async (params: {
  tenderId: string;
  fixes: Array<{ section_id: string; instruction: string | Record<string, unknown> }>;
  clientId: string;
  userId: string;
  teamId?: string;
}) => {
  const response = await httpClient.post<{
    success: boolean;
    message: string;
    impact_report?: ImpactItem[];
    error?: string;
  }>(
    '/sectionChat',
    {
      action: 'apply_batch_fixes',
      tenderId: params.tenderId,
      fixes: params.fixes,
      clientId: params.clientId,
      userId: params.userId,
      teamId: params.teamId,
    },
    { timeout: 0 } // No frontend timeout
  );
  return response;
};

// CV Structured Data Handling

export const updateTenderInsights = async (
  tenderId: string,
  data: {
    title?: string | null;
    tenderIssuer?: string | null;
    state?: string | null;
    bidType?: string | null;
    bidCost?: number | null;
    proposedFee?: number | null;
    tenderValue?: number | null;
    lossReason?: string | null;
    archiveReason?: string | null;
    includeInMetrics?: boolean;
    sharedWith?: string[];
  }
) => {
  if (!tenderId) {
    throw new Error('Tender ID is required');
  }

  // Filter out undefined values to avoid Firestore errors
  const updateData: Record<string, unknown> = { updatedAt: new Date() };
  if (data.title !== undefined) {
    updateData.title = data.title;
  }
  if (data.tenderIssuer !== undefined) {
    updateData.tenderIssuer = data.tenderIssuer;
  }
  if (data.state !== undefined) {
    updateData.state = data.state;
  }
  if (data.bidType !== undefined) {
    updateData.bidType = data.bidType;
  }
  if (data.bidCost !== undefined) {
    updateData.bidCost = data.bidCost;
  }
  if (data.proposedFee !== undefined) {
    updateData.proposedFee = data.proposedFee;
  }
  if (data.tenderValue !== undefined) {
    updateData.tenderValue = data.tenderValue;
  }
  if (data.lossReason !== undefined) {
    updateData.lossReason = data.lossReason;
  }
  if (data.archiveReason !== undefined) {
    updateData.archiveReason = data.archiveReason;
  }
  if (data.includeInMetrics !== undefined) {
    updateData.includeInMetrics = data.includeInMetrics;
  }
  if (data.sharedWith !== undefined) {
    updateData.sharedWith = data.sharedWith;
  }

  const docRef = doc(firestore, 'tenders', tenderId);
  await updateDoc(docRef, updateData);
};

export interface AggregationData {
  totalEstimatedValue: number;
  bidVolume: number;
  wins: number;
  losses: number;
  active: number;
  other: number;
  winRate: number;
  lossRate: number;
}

export interface StrategicInsightsData {
  tenders: Record<string, unknown>[];
  filterOptions: {
    teams: string[];
    sectors: string[];
    clients: string[];
    sources: string[];
    states: string[];
  };
  aggregations: {
    team: Record<string, AggregationData>;
    sector: Record<string, AggregationData>;
    client: Record<string, AggregationData>;
    source: Record<string, AggregationData>;
    tenderType?: Record<string, AggregationData>;
    category?: Record<string, AggregationData>;
    keyword?: Record<string, AggregationData>;
  };
}

export interface StrategicInsightsFilters {
  dateRange: { startDate: Date | null; endDate: Date | null };
  teams: string[];
  sectors: string[];
  clients: string[];
  sources: string[];
  states: string[];
}

export const searchKnowledgeBase = async (params: {
  query: string;
  clientId: string;
  folderPaths?: string[];
  maxResults?: number;
}) => {
  const response = await httpClient.post<
    ApiResponse<{ success: boolean; results?: Record<string, unknown>[] }>
  >(
    '/searchKnowledgeBase',
    {
      ...params,
    },
    {
      headers: {},
    }
  );
  return response;
};

export const getStrategicInsights = async (
  filters: StrategicInsightsFilters
): Promise<StrategicInsightsData> => {
  const response = await httpClient.post<{ data: StrategicInsightsData } | StrategicInsightsData>(
    '/getStrategicInsights',
    filters
  );
  return (response as { data: StrategicInsightsData }).data || (response as StrategicInsightsData);
};

export const getInquiryHistory = async (tenderId: string): Promise<Record<string, unknown>[]> => {
  const { collection, getDocs, limit, orderBy, query } = await import('firebase/firestore');
  const historyRef = collection(firestore, `tenders/${tenderId}/inquiryChat`);
  const q = query(historyRef, orderBy('createdAt', 'asc'), limit(50));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data());
};

export const saveInquiryMessage = async (
  tenderId: string,
  msg: Record<string, unknown>
): Promise<void> => {
  const { collection, doc, serverTimestamp, setDoc } = await import('firebase/firestore');
  const msgRef = doc(collection(firestore, `tenders/${tenderId}/inquiryChat`));
  await setDoc(msgRef, {
    ...msg,
    createdAt: serverTimestamp(),
  });
};
